/**
 * Real text measurement for the Studio wrap engine (text engine v2).
 *
 * The Phase-5 wrap estimated width as chars · fontSize · glyphRatio — fine
 * for one known font, wrong the moment the operator picks arbitrary faces
 * (Archivo Black packs ~2× wider than Barlow Condensed at the same size).
 * CapCut research (2026-07-18 spec) pinned the goal: lines must break where
 * the REAL glyphs overflow the real available width.
 *
 * Both surfaces that wrap — the @remotion/player preview and the headless-
 * Chrome render — are browsers with the same font files loaded, so a
 * canvas-2D `measureText` gives identical widths by construction. Node-side
 * callers (translate budgets) never see this module's browser path: every
 * entry point falls back to the estimate when `document`/canvas is missing
 * or the family isn't loaded yet. A wrap NEVER crashes and NEVER blocks on
 * fonts — `FontFaces` gates the render with delayRender so measurement only
 * happens after faces resolve.
 *
 * Letter-spacing: canvas measureText ignores CSS letter-spacing, so it is
 * added arithmetically — (visible chars − 1) · letterSpacingPx, matching how
 * the browser lays out a run with uniform tracking.
 *
 * PURE data in, number out; the only ambient dependency is the (memoized)
 * shared canvas context. Deterministic for a given font state.
 */

export interface MeasureSpec {
  /** CSS font-family stack, e.g. `"Archivo Black", "AlHurra", sans-serif`. */
  fontFamily: string;
  fontWeight: number;
  fontSizePx: number;
  /** Uniform tracking in px (parsed from the layout's letterSpacing). */
  letterSpacingPx: number;
}

/** Width in px of a visible (tag-free) string under some spec. */
export type TextMeasurer = (text: string) => number;

// ---------------------------------------------------------------------------
// Estimate path (pure — Node-safe, and the browser fallback)
// ---------------------------------------------------------------------------

/** The Phase-5 estimate as a TextMeasurer: chars · fontSize · glyphRatio,
 *  plus tracking. Used whenever real measurement is unavailable. */
export function createEstimateMeasurer(
  fontSizePx: number,
  glyphRatio: number,
  letterSpacingPx = 0,
): TextMeasurer {
  return (text) => {
    const chars = [...text].length;
    if (chars === 0) return 0;
    return chars * fontSizePx * glyphRatio + (chars - 1) * letterSpacingPx;
  };
}

// ---------------------------------------------------------------------------
// Canvas path (browser only)
// ---------------------------------------------------------------------------

let sharedCtx: CanvasRenderingContext2D | null | undefined;

function canvasCtx(): CanvasRenderingContext2D | null {
  if (sharedCtx !== undefined) return sharedCtx;
  try {
    if (typeof document === "undefined") return (sharedCtx = null);
    const canvas = document.createElement("canvas");
    sharedCtx = canvas.getContext("2d");
  } catch {
    sharedCtx = null;
  }
  return sharedCtx ?? null;
}

/** True when `family` has at least one REGISTERED and loaded face.
 *
 *  Gotcha (critic-caught, 2026-07-19): `document.fonts.check()` returns TRUE
 *  for a family with NO registered @font-face at all (the spec treats an
 *  unknown family as a system font) — the exact fail-open this gate exists
 *  to prevent. So first require the family to exist in the FontFaceSet;
 *  only then trust check() for load state. */
export function fontLoaded(family: string, fontSizePx: number, weight: number): boolean {
  try {
    if (typeof document === "undefined" || !document.fonts) return false;
    let registered = false;
    document.fonts.forEach((face) => {
      if (face.family.replace(/^['"]|['"]$/g, "") === family) registered = true;
    });
    if (!registered) return false;
    return document.fonts.check(`${weight} ${fontSizePx}px "${family}"`);
  } catch {
    return false;
  }
}

/**
 * A real canvas measurer for `spec`, or null when the environment can't
 * measure (no DOM) or the PRIMARY family isn't loaded yet (measuring would
 * silently use a fallback face and bake wrong breaks). Callers pair this
 * with `createEstimateMeasurer` as the fallback.
 */
export function createCanvasMeasurer(spec: MeasureSpec): TextMeasurer | null {
  const ctx = canvasCtx();
  if (!ctx) return null;
  const primary = spec.fontFamily.split(",")[0]?.trim().replace(/^['"]|['"]$/g, "");
  if (!primary || !fontLoaded(primary, spec.fontSizePx, spec.fontWeight)) return null;
  ctx.font = `${spec.fontWeight} ${spec.fontSizePx}px ${spec.fontFamily}`;
  const cache = new Map<string, number>();
  return (text) => {
    const hit = cache.get(text);
    if (hit !== undefined) return hit;
    // Re-assert the font — the shared ctx is reused across specs.
    ctx.font = `${spec.fontWeight} ${spec.fontSizePx}px ${spec.fontFamily}`;
    const chars = [...text].length;
    const w = chars === 0
      ? 0
      : ctx.measureText(text).width + Math.max(0, chars - 1) * spec.letterSpacingPx;
    cache.set(text, w);
    return w;
  };
}

/** Parse a CSS letter-spacing value ("−2.25px", "0.5px", "normal") to px. */
export function letterSpacingToPx(value: string | undefined): number {
  if (!value) return 0;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}
